import { Badge } from '@/components/ui/badge';
import { Button } from "@akuna/ui";
import { Checkbox } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Trash2, Minus, Plus } from 'lucide-react';
import { CartItem, SuggestedPackageChargeRow } from '@/hooks/useSales';
import { getItemTypeBadgeClass } from '@/lib/item-type-badge';
import { resolveItemPetId, type SalePet } from '@/lib/sales/cart-pets';
import { getBillingIntentBadgeConfig } from '@/lib/appointment-billing-intent';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CartItemsProps {
  items: CartItem[];
  packageCharges?: SuggestedPackageChargeRow[];
  onUpdateQuantity: (id: string, quantity: number) => void;
  onUpdateUnitPrice: (id: string, unitPrice: number) => void;
  onRemove: (id: string) => void;
  onUpdatePackageCharge?: (id: string, updates: Partial<Pick<SuggestedPackageChargeRow, "included" | "amount">>) => void;
  onRemovePackageCharge?: (id: string) => void;
  professionals?: Array<{ id: string; full_name: string }>;
  onUpdateCartItemProfessional?: (cartItemId: string, professionalId: string | null, professionalName: string | null) => void;
  /** Pets do responsável; o select "Pet" só aparece com dois ou mais. */
  pets?: SalePet[];
  salePetId?: string | null;
  onUpdateCartItemPet?: (cartItemId: string, petId: string | null) => void;
  /**
   * Item → profissionais que ganham comissão nele. Com o mapa, o select "Profissional" só aparece em item com
   * quem ganhe e lista só esses; `null`/ausente (mapa indisponível) mantém todos os profissionais.
   */
  commissionByItem?: Map<string, Set<string>> | null;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatBrazilianNumber = (value: number) => value.toFixed(2).replace('.', ',');
const formatCents = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function CartRegularItemRow({
  item,
  badge,
  onUpdateQuantity,
  onUpdateUnitPrice,
  onRemove,
  finalItemTotalLabel,
  finalUnitPriceLabel,
  eachLabel,
  decreaseQuantityLabel,
  increaseQuantityLabel,
  removeItemLabel,
  professionals,
  onUpdateProfessional,
  canEarnCommission,
  pets,
  petId,
  onUpdatePet,
}: {
  item: CartItem;
  badge: { label: string; className: string };
  onUpdateQuantity: (id: string, quantity: number) => void;
  onUpdateUnitPrice: (id: string, unitPrice: number) => void;
  onRemove: (id: string) => void;
  finalItemTotalLabel: string;
  finalUnitPriceLabel: string;
  eachLabel: string;
  decreaseQuantityLabel: string;
  increaseQuantityLabel: string;
  removeItemLabel: string;
  professionals?: Array<{ id: string; full_name: string }>;
  onUpdateProfessional?: (professionalId: string | null, professionalName: string | null) => void;
  canEarnCommission?: boolean;
  pets?: SalePet[];
  petId?: string | null;
  onUpdatePet?: (petId: string | null) => void;
}) {
  const { t } = useTranslation("erp");
  const { t: tCommission } = useTranslation("commissions");
  const professionalList = professionals ?? [];
  const petList = pets ?? [];
  const showPetSelect = petList.length > 1;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [displayTotal, setDisplayTotal] = useState(() => (item.total > 0 ? formatBrazilianNumber(item.total) : ''));
  const [isEditingTotal, setIsEditingTotal] = useState(false);
  const [cents, setCents] = useState(() => Math.round(item.total * 100));

  useEffect(() => {
    if (!isEditingTotal) {
      const nextCents = Math.round(item.total * 100);
      setCents(nextCents);
      setDisplayTotal(nextCents > 0 ? formatCents(nextCents) : '');
    }
  }, [item.total, isEditingTotal]);

  const applyCents = (nextCents: number) => {
    const clampedCents = Math.max(0, Math.min(nextCents, 999999999));
    setCents(clampedCents);
    setDisplayTotal(clampedCents === 0 ? '' : formatCents(clampedCents));
    const nextTotal = clampedCents / 100;
    const nextUnitPrice = item.quantity > 0 ? nextTotal / item.quantity : 0;
    onUpdateUnitPrice(item.id, nextUnitPrice);
  };

  const setCaretFromDigitsRight = (digitsRight: number) => {
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;

      const after = input.value;
      if (digitsRight <= 0) {
        input.setSelectionRange(after.length, after.length);
        return;
      }

      let counted = 0;
      let newPos = after.length;
      for (let index = after.length - 1; index >= 0; index -= 1) {
        if (/\d/.test(after[index])) counted += 1;
        if (counted === digitsRight) {
          newPos = index;
          break;
        }
      }
      input.setSelectionRange(newPos, newPos);
    });
  };

  const handleMoneyKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const value = input.value;
    const selectionStart = input.selectionStart ?? value.length;

    if (event.ctrlKey || event.metaKey || event.altKey) return;

    if (event.key === 'Tab' || event.key.startsWith('Arrow') || event.key === 'Home' || event.key === 'End' || event.key === 'Enter' || event.key === 'Escape') {
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      let index = selectionStart - 1;
      while (index >= 0 && !/\d/.test(value[index])) index -= 1;
      if (index < 0) return;

      const digitsAfter = value.slice(index + 1).replace(/\D/g, '').length;
      const centsStr = String(cents).padStart(3, '0');
      const digitIdx = centsStr.length - 1 - digitsAfter;
      const nextStr = centsStr.slice(0, digitIdx) + centsStr.slice(digitIdx + 1);
      applyCents(parseInt(nextStr || '0', 10));
      setCaretFromDigitsRight(digitsAfter);
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      let index = selectionStart;
      while (index < value.length && !/\d/.test(value[index])) index += 1;
      if (index >= value.length) return;

      const digitsAfter = value.slice(index + 1).replace(/\D/g, '').length;
      const centsStr = String(cents).padStart(3, '0');
      const digitIdx = centsStr.length - 1 - digitsAfter;
      const nextStr = centsStr.slice(0, digitIdx) + centsStr.slice(digitIdx + 1);
      applyCents(parseInt(nextStr || '0', 10));
      setCaretFromDigitsRight(digitsAfter);
      return;
    }

    if (event.key.length === 1 && /\d/.test(event.key)) {
      event.preventDefault();
      const digitsRight = value.slice(selectionStart).replace(/\D/g, '').length;
      const centsStr = String(cents).padStart(3, '0');
      const insertAt = centsStr.length - digitsRight;
      const nextStr = centsStr.slice(0, insertAt) + event.key + centsStr.slice(insertAt);
      applyCents(parseInt(nextStr, 10));
      setCaretFromDigitsRight(digitsRight);
      return;
    }

    if (event.key.length === 1) {
      event.preventDefault();
    }
  };

  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="flex gap-4 flex-row lg:items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-row items-center gap-2">
            <h4 className="truncate text-base font-semibold text-foreground">{item.name}</h4>
            <Badge className={badge.className}>{badge.label}</Badge>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="icon"
            variant="outline"
            aria-label={decreaseQuantityLabel}
            onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Input
            type="number"
            value={item.quantity}
            onChange={(e) => onUpdateQuantity(item.id, parseInt(e.target.value) || 0)}
            className="w-16 text-center"
            min="0"
          />
          <Button
            size="icon"
            variant="outline"
            aria-label={increaseQuantityLabel}
            onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label={removeItemLabel}
            onClick={() => onRemove(item.id)}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      <div className="mt-4 flex gap-6 border-t pt-4 flex-col">
        <div className="flex gap-6 flex-row items-start justify-between">
        <div className="flex flex-col justify-end gap-1">
          <p className="text-xs uppercase tracking-[0.18em] text-color-light">{finalUnitPriceLabel}</p>
          <p className="text-sm font-[500] text-color-medium">{formatCurrency(item.unitPrice)}</p>
        </div>

        <div className="w-max max-w-full shrink-0 text-right">
        <p className="text-xs uppercase tracking-[0.18em] text-color-light mb-1">{finalItemTotalLabel}</p>
          <div className="flex w-max max-w-full items-end rounded-2xl border border-border bg-muted/20 px-4 py-3 shadow-sm transition-colors focus-within:border-primary focus-within:bg-background">
            <span className="text-md font-semibold text-muted-foreground">R$</span>
            {/* Span sets outer width (text + same px as input) so padding does not clip value/caret */}
            <div className="relative inline-block max-w-full overflow-visible align-bottom">
              {/* Same pl/pr + tabular-nums as input; thin space buffers subpixel glyph overflow vs input paint */}
              <span
                className="invisible block whitespace-pre pl-2 pr-1 !text-xl font-bold tabular-nums leading-none tracking-tight"
                aria-hidden
              >
                {`${displayTotal || '0,00'}\u2009`}
              </span>
              <Input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                value={displayTotal}
                onFocus={() => {
                  setIsEditingTotal(true);
                  if (cents === 0) {
                    setDisplayTotal('');
                  }
                }}
                onKeyDown={handleMoneyKeyDown}
                onChange={() => {}}
                onBlur={() => {
                  setIsEditingTotal(false);
                  setDisplayTotal(cents > 0 ? formatCents(cents) : '');
                }}
                className="absolute inset-0 box-border !block h-full min-h-0 min-w-0 w-full overflow-x-visible overflow-y-visible border-0 bg-transparent pl-2 pr-1 !text-xl font-bold tabular-nums leading-none tracking-tight text-foreground shadow-none focus-visible:ring-0"
                placeholder="0,00"
                aria-label={finalItemTotalLabel}
              />
            </div>
          </div>
        </div>
        </div>
        {(showPetSelect || canEarnCommission) && (
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            {showPetSelect && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-foreground" htmlFor={`pet-${item.id}`}>
                  {t("cart.pet")}
                </label>
                <select
                  id={`pet-${item.id}`}
                  className="h-10 rounded-md border border-border bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={petId ?? ""}
                  onChange={(e) => onUpdatePet?.(e.target.value || null)}
                >
                  {petList.map((pet) => (
                    <option key={pet.id} value={pet.id}>{pet.name}</option>
                  ))}
                </select>
              </div>
            )}
            {canEarnCommission && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-foreground" htmlFor={`pro-${item.id}`}>
                  {tCommission("pos.professional")}
                </label>
                <select
                  id={`pro-${item.id}`}
                  disabled={professionalList.length === 0}
                  className="h-10 rounded-md border border-border bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={professionalList.some((p) => p.id === item.professionalId) ? (item.professionalId ?? "") : ""}
                  onChange={(e) => {
                    const prof = professionalList.find((p) => p.id === e.target.value) ?? null;
                    onUpdateProfessional?.(prof?.id ?? null, prof?.full_name ?? null);
                  }}
                >
                  <option value="">{tCommission("pos.professionalPlaceholder")}</option>
                  {professionalList.map((p) => (
                    <option key={p.id} value={p.id}>{p.full_name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const getItemTypeBadge = (type: CartItem["itemType"] | undefined, labels: Record<"product" | "service" | "package" | "item", string>) => {
  if (type === "PRODUTO") return { label: labels.product, className: getItemTypeBadgeClass("PRODUTO")! };
  if (type === "SERVICO") return { label: labels.service, className: getItemTypeBadgeClass("SERVICO")! };
  if (type === "PACOTE") return { label: labels.package, className: getItemTypeBadgeClass("PACOTE")! };
  return { label: labels.item, className: "bg-slate-100 text-slate-700 hover:bg-slate-100" };
};

const getPackageIntentBadge = (charge: SuggestedPackageChargeRow) => {
  if (charge.billingIntent === "integral_now" && charge.packageMode === "new" && !charge.appointmentPatientPackageId) {
    return null;
  }

  return getBillingIntentBadgeConfig(charge.billingIntent);
};

export const CartItems = ({
  items,
  packageCharges = [],
  onUpdateQuantity,
  onUpdateUnitPrice,
  onRemove,
  onUpdatePackageCharge,
  onRemovePackageCharge,
  professionals,
  onUpdateCartItemProfessional,
  pets,
  salePetId,
  onUpdateCartItemPet,
  commissionByItem,
}: CartItemsProps) => {
  const { t } = useTranslation("erp");
  const regularItems = items.filter((item) => item.itemType !== "PACOTE");
  const cartPackageItems = items.filter((item) => item.itemType === "PACOTE");
  const totalRows = items.length + packageCharges.length;

  if (totalRows === 0) {
    return (
      <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground">
        {t("cart.empty")}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {regularItems.map((item) => {
        const badge = getItemTypeBadge(item.itemType, {
          product: t("common.product"),
          service: t("common.service"),
          package: t("common.package"),
          item: t(item.itemId ? "common.item" : "adHocItem.badge"),
        });

        const earners = commissionByItem && item.itemId ? commissionByItem.get(item.itemId) : undefined;
        const rowProfessionals = commissionByItem
          ? (professionals ?? []).filter((p) => earners?.has(p.id))
          : professionals;
        const rowCanEarnCommission = commissionByItem
          ? Boolean(item.canEarnCommission) && (rowProfessionals?.length ?? 0) > 0
          : item.canEarnCommission;

        return (
          <CartRegularItemRow
            key={item.id}
            item={item}
            badge={badge}
            onUpdateQuantity={onUpdateQuantity}
            onUpdateUnitPrice={onUpdateUnitPrice}
            onRemove={onRemove}
            finalItemTotalLabel={t("cart.finalItemTotal")}
            finalUnitPriceLabel={t("cart.finalUnitPrice")}
            eachLabel={t("cart.each", { amount: formatCurrency(item.unitPrice) })}
            decreaseQuantityLabel={t("cart.decreaseQuantity")}
            increaseQuantityLabel={t("cart.increaseQuantity")}
            removeItemLabel={t("cart.removeItem")}
            professionals={rowProfessionals}
            canEarnCommission={rowCanEarnCommission}
            onUpdateProfessional={(profId, profName) => onUpdateCartItemProfessional?.(item.id, profId, profName)}
            pets={pets}
            petId={resolveItemPetId(item, salePetId ?? null)}
            onUpdatePet={(nextPetId) => onUpdateCartItemPet?.(item.id, nextPetId)}
          />
        );
      })}

      {cartPackageItems.map((item) => (
        <div key={item.id} className="rounded-md border p-4 flex flex-col gap-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="font-medium truncate">{item.name}</h4>
                <Badge className={getItemTypeBadgeClass("PACOTE")!}>{t("common.package")}</Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("cart.packageTotal", { amount: formatCurrency(item.total) })}
              </p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Button
                size="icon"
                variant="ghost"
                aria-label={t("common.remove")}
                onClick={() => onRemove(item.id)}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t pt-3">
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{t("cart.quantity")}</span> {item.quantity}
            </div>
            <div className="text-lg font-semibold text-primary text-right">
              {formatCurrency(item.total)}
            </div>
          </div>
        </div>
      ))}

      {packageCharges.map((charge) => {
        const packageAmountInput = Math.max(0, Number(charge.amount) || 0);
        const remainingAfterSale = Math.max(0, charge.fullPrice - packageAmountInput);
        const packageIntentBadge = getPackageIntentBadge(charge);

        return (
          <div key={charge.id} className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="flex flex-row justify-between gap-4 lg:items-center">
              <div className="min-w-0 flex-1">
                <div className="flex flex-row flex-wrap items-center gap-2">
                  <h4 className="truncate text-base font-semibold text-foreground">{charge.title}</h4>
                  <Badge className={getItemTypeBadgeClass("PACOTE")!}>{t("common.package")}</Badge>
                  {packageIntentBadge?.label && (
                    <Badge className={packageIntentBadge?.className}>{packageIntentBadge?.label}</Badge>
                  )}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("cart.packageTotal", { amount: formatCurrency(charge.fullPrice) })}
                </p>
              </div>
              <Button
                size="icon"
                variant="ghost"
                aria-label={t("common.remove")}
                onClick={() => onRemovePackageCharge?.(charge.id)}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>

            <div className="mt-4 flex flex-col gap-3 border-t pt-4">
              <div className="flex gap-6 border-t-0 pt-0 flex-row items-start justify-between">
                <div className="flex flex-col justify-end gap-1">
                  <p className="text-xs uppercase tracking-[0.18em] text-color-light">
                    {t("cart.remainingBalance")}
                  </p>
                  <p className="text-sm font-[500] text-color-medium">{formatCurrency(remainingAfterSale)}</p>
                  <div className="flex items-center gap-2 pt-5">
                  <Checkbox
                    aria-label={t("cart.chargeThisInstallment")}
                    checked={charge.included}
                    onCheckedChange={(checked) =>
                      onUpdatePackageCharge?.(charge.id, { included: checked === true })
                    }
                  />
                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                    {charge.included ? t("cart.chargeThisSale") : t("cart.keepVisible")}
                  </p>
                </div>
                </div>

                

                <div className="w-max max-w-full shrink-0 text-right">
                  <p className="text-xs uppercase tracking-[0.18em] text-color-light mb-1">
                    {t("cart.finalItemTotal")}
                  </p>
                  <div className="flex w-max max-w-full items-end rounded-2xl border border-border bg-muted/20 px-4 py-3 shadow-sm transition-colors focus-within:border-primary focus-within:bg-background">
                    <span className="text-md font-semibold text-muted-foreground">R$</span>
                    <div className="relative inline-block max-w-full overflow-visible align-bottom">
                      <span
                        className="invisible block whitespace-pre pl-2 pr-1 !text-xl font-bold tabular-nums leading-none tracking-tight"
                        aria-hidden
                      >
                        {`${formatBrazilianNumber(packageAmountInput) || '0,00'}\u2009`}
                      </span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={packageAmountInput}
                        onChange={(event) =>
                          onUpdatePackageCharge?.(charge.id, { amount: Number(event.target.value) || 0 })
                        }
                        disabled={!charge.included}
                        className="absolute inset-0 box-border !block h-full min-h-0 min-w-0 w-full overflow-x-visible overflow-y-visible border-0 bg-transparent pl-2 pr-1 !text-xl font-bold tabular-nums leading-none tracking-tight text-foreground shadow-none focus-visible:ring-0 text-right"
                        aria-label={t("cart.packageAmountThisSale")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
